import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLocationPath } from '@/hooks/useLocationNavigate';
import {
  LayoutDashboard,
  Users,
  Calendar,
  BarChart3,
  Settings,
  Shield,
  GraduationCap,
  LogOut,
  User,
  Pin,
  PinOff,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from 'shared';

interface NavItem {
  icon: LucideIcon;
  path: string;
  label: string;
  roles?: UserRole[]; // If set, only these roles can see this item
}

const navItems: NavItem[] = [
  { icon: LayoutDashboard, path: '/', label: 'Übersicht' },
  { icon: BarChart3, path: '/statistics', label: 'Statistiken', roles: ['super_admin', 'location_admin'] },
  { icon: Users, path: '/contacts', label: 'Kontakte' },
  { icon: Calendar, path: '/calendar', label: 'Kalender' },
  { icon: GraduationCap, path: '/schulung', label: 'Schulung' },
  { icon: Shield, path: '/admin', label: 'Administration', roles: ['super_admin', 'location_admin'] },
  { icon: Settings, path: '/settings', label: 'Einstellungen' },
];

// Storage key for sidebar state
export const SIDEBAR_STORAGE_KEY = 'vitbikes-sidebar-expanded';

export interface SidebarProps {
  isExpanded: boolean;
  isPinned?: boolean;
  onToggle: () => void;
}

export function Sidebar({ isExpanded, isPinned = false, onToggle }: SidebarProps) {
  const location = useLocation();
  const locationPath = useLocationPath();
  const { data: currentUser } = useCurrentUser();
  const { logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // Filter nav items based on user role
  const visibleNavItems = navItems.filter(item => {
    if (!item.roles) return true;
    return currentUser && item.roles.includes(currentUser.role);
  });

  // Get user initials
  const userInitials = currentUser
    ? `${currentUser.firstName?.[0] || ''}${currentUser.lastName?.[0] || ''}`
    : 'U';

  return (
    <aside className="h-full bg-slate-800 flex flex-col overflow-hidden">
      {/* Logo & Brand */}
      <div className="h-16 flex items-center border-b border-slate-700 px-3">
        <div className={cn(
          "flex items-center gap-3 transition-all duration-300",
          isExpanded ? "justify-start" : "justify-center w-full"
        )}>
          <img
            src="/fav.png"
            alt="vit:bikes"
            className="w-10 h-10 object-contain shrink-0"
          />
          {isExpanded && (
            <span className="text-lg font-bold text-white whitespace-nowrap overflow-hidden animate-fade-in">
              vit:bikes
            </span>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
        <ul className="space-y-1 px-2">
          {visibleNavItems.map((item) => {
            // Strip /location/:slug prefix to compare against item.path
            const subPath = location.pathname.replace(/^\/location\/[^/]+/, '') || '/';
            const isActive = subPath === item.path ||
              (item.path !== '/' && subPath.startsWith(item.path));

            return (
              <li key={item.path}>
                <Link
                  to={locationPath(item.path)}
                  className={cn(
                    'flex items-center h-12 rounded-lg transition-colors relative group',
                    isExpanded ? 'px-3 gap-3' : 'justify-center',
                    isActive
                      ? 'bg-slate-700 text-orange-500'
                      : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-200'
                  )}
                  title={!isExpanded ? item.label : undefined}
                >
                  {/* Active indicator */}
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-orange-500 rounded-r-full" />
                  )}
                  <item.icon className="w-5 h-5 shrink-0" />

                  {/* Label - shown when expanded */}
                  {isExpanded && (
                    <span className="text-sm font-medium whitespace-nowrap overflow-hidden">
                      {item.label}
                    </span>
                  )}

                  {/* Tooltip - only when collapsed */}
                  {!isExpanded && (
                    <span className="absolute left-full ml-2 px-2 py-1 bg-slate-700 text-slate-200 text-sm rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                      {item.label}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Pin/Unpin Button */}
      <div className="px-2 py-2 border-t border-slate-700">
        <button
          onClick={onToggle}
          className={cn(
            "flex items-center h-10 rounded-lg transition-colors w-full",
            isPinned
              ? "text-orange-400 hover:bg-slate-700/50 hover:text-orange-300"
              : "text-slate-400 hover:bg-slate-700/50 hover:text-slate-200",
            isExpanded ? "px-3 gap-3 justify-start" : "justify-center"
          )}
          title={isPinned ? "Sidebar lösen" : "Sidebar fixieren"}
        >
          {isPinned ? (
            <>
              <PinOff className="w-5 h-5 shrink-0" />
              {isExpanded && <span className="text-sm font-medium">Lösen</span>}
            </>
          ) : (
            <>
              <Pin className="w-5 h-5 shrink-0" />
              {isExpanded && <span className="text-sm font-medium">Fixieren</span>}
            </>
          )}
        </button>
      </div>

      {/* User section at bottom */}
      <div className={cn(
        "p-3 border-t border-slate-700 relative",
        isExpanded ? "flex items-center gap-3" : ""
      )} ref={menuRef}>
        <button
          onClick={() => setShowUserMenu(!showUserMenu)}
          className={cn(
            "w-10 h-10 rounded-full bg-linear-to-br from-blue-500 to-purple-500 flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-orange-500 transition-all shrink-0",
            !isExpanded && "mx-auto"
          )}
          aria-label="Benutzermenü"
        >
          <span className="text-white font-semibold text-sm">{userInitials}</span>
        </button>

        {/* User info when expanded */}
        {isExpanded && currentUser && (
          <div className="flex-1 min-w-0 overflow-hidden">
            <p className="text-sm font-medium text-slate-200 truncate">
              {currentUser.firstName} {currentUser.lastName}
            </p>
            <p className="text-xs text-slate-400 truncate">{currentUser.email}</p>
          </div>
        )}

        {/* User Dropdown Menu */}
        {showUserMenu && (
          <div className={cn(
            "absolute bottom-full mb-2 w-48 bg-slate-700 rounded-lg shadow-lg border border-slate-600 overflow-hidden z-50",
            isExpanded ? "left-3" : "left-0"
          )}>
            {/* User info */}
            <div className="px-3 py-2 border-b border-slate-600">
              <p className="text-sm font-medium text-slate-200 truncate">
                {currentUser?.firstName} {currentUser?.lastName}
              </p>
              <p className="text-xs text-slate-400 truncate">{currentUser?.email}</p>
            </div>

            {/* Menu items */}
            <div className="py-1">
              <Link
                to={locationPath('/settings')}
                onClick={() => setShowUserMenu(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-600 transition-colors"
              >
                <User className="w-4 h-4" />
                Profil
              </Link>
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  logout();
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-slate-600 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Abmelden
              </button>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
